// employmentContractService.js
// Dedicated Service Layer for Employment Contracts API

import axiosInstance from "../../../../common/axiosInstance";

// Helper to calculate days left from end_date
export const calculateDaysLeft = (endDateStr) => {
  if (!endDateStr) return null;
  const endDate = new Date(endDateStr);
  if (isNaN(endDate.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  const diffTime = endDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Normalize / Transform raw API contract record into standard UI contract object
const normalizeContract = (raw) => {
  if (!raw) return null;

  const startDate = raw.start_date || raw.startDate || null;
  const endDate = raw.end_date || raw.endDate || null;
  const daysLeft =
    raw.days_left !== undefined && raw.days_left !== null
      ? Number(raw.days_left)
      : calculateDaysLeft(endDate);

  return {
    uuid: raw.uuid || raw.id || null,
    employee_id: raw.employee_id || raw.employee_code || null,
    employee_name: raw.employee_name || raw.employee?.full_name || raw.full_name || "Unknown",
    avatar: raw.avatar || raw.employee?.avatar || null,
    job_title: raw.job_title || raw.employee?.job_title || raw.designation || null,
    department: raw.department || raw.employee?.department || "Unassigned",
    contract_type: raw.contract_type || raw.contractType || "N/A",
    employment_type: raw.employment_type || raw.employmentType || "N/A",
    work_schedule: raw.work_schedule || raw.workSchedule || "N/A",
    start_date: startDate,
    end_date: endDate,
    duration: raw.duration || null,
    status: raw.status || "Unknown",
    days_left: daysLeft || 0,
    probation_period: raw.probation_period || null,
    probation_end_date: raw.probation_end_date || null,
    renewal_notice_period: raw.renewal_notice_period || null,
    created_by: raw.created_by || null,
    created_date: raw.created_at || raw.created_date || null,
    attachments: raw.attachments || [],
    history: raw.history || [],
    documents: raw.documents || [],
  };
};

// Fetch list of employment contracts from API
export const getEmploymentContracts = async (params = {}) => {
  try {
    // 1. Fetch all employees and employment types concurrently
    const [empResponse, typeRes] = await Promise.all([
      axiosInstance.get("/employees", { params }),
      axiosInstance.get("/employment/employment-types").catch(() => null)
    ]);

    const employeesList = Array.isArray(empResponse?.data?.data)
      ? empResponse.data.data
      : Array.isArray(empResponse?.data)
      ? empResponse.data
      : [];
    
    const types = Array.isArray(typeRes?.data?.data) ? typeRes.data.data : [];
    
    // 2. Fetch assignments for each employee to extract contract details
    const contractPromises = employeesList.map(async (emp) => {
      try {
        const assignResponse = await axiosInstance.get(`/employees/${emp.uuid}/organisation-assignments`);
        const assignments = assignResponse?.data?.data || assignResponse?.data || [];
        const primary = assignments.find((a) => a.is_primary) || assignments[0];
        
        if (primary) {
          // Find matching employment type from backend to get probation/notice defaults
          const matchedType = types.find(t => 
            t.uuid === primary.employment_type_uuid || 
            t.type_code === primary.employment_type ||
            t.type_name?.toLowerCase().includes((primary.employment_type || "").toLowerCase())
          );

          const probationDays = matchedType?.default_probation_days || 0;
          let probationEndDate = null;
          
          if (probationDays > 0 && primary.effective_from) {
            const date = new Date(primary.effective_from);
            date.setDate(date.getDate() + probationDays);
            probationEndDate = date.toISOString().split("T")[0];
          }

          return {
            uuid: primary.uuid, // Use assignment UUID as contract UUID
            employee_id: emp.employee_code,
            employee_name: emp.full_name,
            avatar: emp.avatar,
            department: primary.department_uuid, 
            contract_type: primary.employment_type === "intern" ? "Internship" : (matchedType?.type_name || "Contract"),
            employment_type: primary.employment_type || matchedType?.type_name,
            start_date: primary.effective_from,
            end_date: primary.effective_to,
            status: primary.status || "Active",
            created_at: primary.created_at,
            created_by: primary.created_by || "HR Administrator",
            probation_period: probationDays > 0 ? `${probationDays} Days` : "—",
            probation_end_date: probationEndDate,
            renewal_notice_period: matchedType?.default_notice_period_days ? `${matchedType.default_notice_period_days} Days` : "—",
            work_schedule: "Standard",
          };
        }
      } catch (err) {
        console.warn(`Failed to fetch assignments for employee ${emp.uuid}`);
      }
      return null;
    });

    const employeesWithContractsRaw = await Promise.all(contractPromises);
    let validContractsRaw = employeesWithContractsRaw.filter(Boolean);
    
    let contracts = validContractsRaw.map(normalizeContract);

    if (contracts.length === 0 && employeesList.length > 0) {
      contracts = employeesList.map((emp) => ({
        uuid: emp.uuid || `cntr-${emp.employee_code || "0001"}`,
        employee_id: emp.employee_code || emp.employee_id || "EMP-0001",
        employee_name: emp.full_name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
        avatar: emp.avatar || null,
        job_title: emp.designation || emp.remarks || "Software Developer",
        department: emp.department_name || "Engineering",
        contract_type: emp.contract_type || "Permanent Agreement",
        employment_type: emp.employment_type_name || "Full-Time Permanent",
        work_schedule: emp.work_schedule_name || "General Shift (09:00 - 18:00)",
        start_date: emp.start_date || emp.joining_date || "2026-01-01",
        end_date: emp.end_date || null,
        status: "Active",
        days_left: emp.end_date ? calculateDaysLeft(emp.end_date) : null,
      }));
    }
    
    return {
      success: true,
      data: contracts,
      total: contracts.length,
    };
  } catch (err) {
    console.error("Failed to fetch employment contracts via employees API:", err);
    return {
      success: false,
      data: [],
      total: 0,
    };
  }
};

// Fetch Contract Statistics from API or calculate dynamically
export const getContractStats = async (contractsList = []) => {

  const list = contractsList || [];
  const total = list.length;
  const active = list.filter(
    (c) => String(c.status).toLowerCase() === "active"
  ).length;
  const expiringSoon = list.filter(
    (c) =>
      String(c.status).toLowerCase() === "expiring soon" ||
      (c.days_left !== null && c.days_left >= 0 && c.days_left <= 60)
  ).length;
  const expired = list.filter(
    (c) =>
      String(c.status).toLowerCase() === "expired" ||
      (c.days_left !== null && c.days_left < 0)
  ).length;
  const activePercentage = total > 0 ? `${((active / total) * 100).toFixed(1)}% of total` : "0%";

  return {
    total: total,
    active: active,
    activePercentage: activePercentage,
    expiringSoon: expiringSoon,
    expired: expired,
  };
};

/**
 * Fetch contract details by UUID
 */
export const getEmploymentContractById = async (uuid) => {
  try {
    const response = await axiosInstance.get(`/employment/employment-contracts/${uuid}`);
    if (response?.data?.data) {
      return normalizeContract(response.data.data);
    }
  } catch (err) {
    console.error(`Fetch contract ${uuid} failed:`, err.message);
  }
  return null;
};

/**
 * Create new employment contract
 */
export const createEmploymentContract = async (payload) => {
  try {
    // 1. Fetch employee to get their assignment UUID
    const empRes = await axiosInstance.get("/employees");
    const allEmployees = Array.isArray(empRes?.data?.data) ? empRes.data.data : [];
    const matchedEmployee = allEmployees.find((e) => {
      const code1Str = (e.employee_code || "").replace(/\D/g, "");
      const code2Str = (payload.employee_id || "").replace(/\D/g, "");
      const code1 = parseInt(code1Str, 10);
      const code2 = parseInt(code2Str, 10);
      
      const isCodeMatch = !isNaN(code1) && !isNaN(code2) && code1 === code2;
      const isNameMatch = payload.employee_name && e.full_name?.toLowerCase().includes(payload.employee_name.toLowerCase());
      
      return isCodeMatch || isNameMatch;
    });

    let assignmentUuid = null;
    if (matchedEmployee) {
      const assignRes = await axiosInstance.get(`/employees/${matchedEmployee.uuid}/organisation-assignments`);
      const assignments = assignRes?.data?.data || assignRes?.data || [];
      const primary = assignments.find((a) => a.is_primary) || assignments[0];
      if (primary) {
        assignmentUuid = primary.uuid;
      }
    }

    if (!assignmentUuid) {
      throw new Error(`Cannot create contract: Employee ${payload.employee_name || payload.employee_id} does not have an active organisation assignment yet.`);
    }

    // 2. Fetch employment types to get the employment_type_uuid
    const typeRes = await axiosInstance.get("/employment/employment-types");
    const types = Array.isArray(typeRes?.data?.data) ? typeRes.data.data : [];
    
    // Attempt to match the UI's contract type (or employment type) with backend type names
    // e.g. "Full Time" -> FT, "Part Time" -> PT, "Contract" -> CT
    const searchType = payload.contract_type === "Permanent" ? "Full Time" : payload.contract_type;
    const matchedType = types.find((t) => 
      t.type_name?.toLowerCase().includes(searchType?.toLowerCase()) || 
      t.type_code === searchType
    ) || types[0];

    const typeUuid = matchedType?.uuid;

    if (!typeUuid) {
      throw new Error("Could not find a valid employment type in the backend.");
    }

    // 3. Construct backend payload
    const backendPayload = {
      employee_assignment_uuid: assignmentUuid,
      employment_type_uuid: typeUuid,
      contract_number: `CON-${Date.now().toString().slice(-6)}`,
      start_date: payload.start_date,
      end_date: payload.end_date || null,
    };

    const response = await axiosInstance.post("/employment/employment-contracts", backendPayload);
    
    const createdContract = response?.data?.data || {};
    const probationDays = matchedType?.default_probation_days || 0;
    let probationEndDate = null;
    
    if (probationDays > 0 && payload.start_date) {
      const date = new Date(payload.start_date);
      date.setDate(date.getDate() + probationDays);
      probationEndDate = date.toISOString().split("T")[0];
    }

    const synthesizedRaw = {
      ...createdContract,
      employee_id: payload.employee_id,
      employee_name: payload.employee_name,
      avatar: matchedEmployee?.avatar,
      department: payload.department,
      contract_type: payload.contract_type,
      employment_type: payload.employment_type,
      work_schedule: payload.work_schedule,
      probation_period: probationDays > 0 ? `${probationDays} Days` : null,
      probation_end_date: probationEndDate,
      renewal_notice_period: matchedType?.default_notice_period_days ? `${matchedType.default_notice_period_days} Days` : null,
      created_by: "HR Administrator",
      created_at: createdContract.created_at || new Date().toISOString(),
    };

    // Map response back to UI object format
    return {
      success: true,
      data: normalizeContract(synthesizedRaw),
    };
  } catch (err) {
    console.error("Create contract API failed:", err.response?.data || err.message);
    throw err;
  }
};

/**
 * Update contract
 */
export const updateEmploymentContract = async (uuid, payload) => {
  try {
    const response = await axiosInstance.put(`/employment/employment-contracts/${uuid}`, payload);
    return {
      success: true,
      data: response?.data?.data ? normalizeContract(response.data.data) : payload,
    };
  } catch (err) {
    console.error("Update contract API failed:", err.message);
    throw err;
  }
};

/**
 * Renew employment contract
 */
export const renewEmploymentContract = async (uuid, renewalData) => {
  try {
    const response = await axiosInstance.post(
      `/employment/employment-contracts/${uuid}/renew`,
      renewalData
    );
    return {
      success: true,
      data: response?.data?.data || renewalData,
    };
  } catch (err) {
    console.error("Renew contract API failed:", err.message);
    throw err;
  }
};

/**
 * Delete employment contract
 */
export const deleteEmploymentContract = async (uuid) => {
  try {
    await axiosInstance.delete(`/employment/employment-contracts/${uuid}`);
    return { success: true, uuid };
  } catch (err) {
    console.error("Delete contract API failed:", err.message);
    throw err;
  }
};

/**
 * Fetch filter options
 */
export const getFilterOptions = async () => {
  try {
    const deptsRes = await axiosInstance.get("/departments");
    if (deptsRes?.data?.data && Array.isArray(deptsRes.data.data)) {
      const depts = ["All", ...deptsRes.data.data.map((d) => d.name || d.department_name)];
      return {
        statuses: ["All", "Active", "Expiring Soon", "Expired", "Pending"],
        types: ["All", "Permanent", "Contract", "Internship", "Temporary"],
        employmentTypes: ["Full Time", "Part Time", "Contract", "Intern"],
        workSchedules: ["Standard", "Flexible", "Shift A", "Shift B"],
        departments: depts,
      };
    }
  } catch (err) {
    console.error("Get departments API failed:", err.message);
  }

  return {
    statuses: ["All", "Active", "Expiring Soon", "Expired", "Pending"],
    types: ["All", "Permanent", "Contract", "Internship", "Temporary"],
    employmentTypes: ["Full Time", "Part Time", "Contract", "Intern"],
    workSchedules: ["Standard", "Flexible", "Shift A", "Shift B"],
    departments: ["All"],
  };
};

/**
 * Export contracts file download
 */
export const exportContractsData = async (format = "csv") => {
  try {
    const response = await axiosInstance.get(`/employment/employment-contracts/export?format=${format}`, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Employment_Contracts_${Date.now()}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    return { success: true };
  } catch (err) {
    console.error("Export API endpoint failed:", err.message);
    throw err;
  }
};
